/**
 * Notification previews iframe the destination page. Deep links often strip
 * their query params once a panel opens, and the target row can render late.
 * Snapshot the ids up front and keep scrolling that element into view.
 */

function cssIdent(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}

function pushId(selectors: string[], id: string, prefix: string): void {
  const trimmed = id.trim();
  if (!trimmed) return;
  selectors.push(`#${prefix}${cssIdent(trimmed)}`);
}

/** Element selectors for the comment, quote row, or record this preview should show. */
export function notificationEmbedFocusSelectors(search: string): string[] {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  if (params.get('notifEmbed') !== '1') return [];

  const selectors: string[] = [];
  const submission = params.get('submission')?.trim();
  if (submission) selectors.push(`[data-rfq-submission-id="${cssIdent(submission)}"]`);

  const taskComment = params.get('taskComment')?.trim() || '';
  if (taskComment) {
    pushId(selectors, taskComment, 'hub-task-comment-');
    selectors.push(`#hub-task-comment-${cssIdent(taskComment)}-pinned`);
  }

  const comment = params.get('comment')?.trim() || '';
  if (comment) {
    pushId(selectors, comment, 'hub-pdf-comment-');
    pushId(selectors, comment, 'lead-team-comment-');
    pushId(selectors, comment, 'ops-comment-');
    pushId(selectors, comment, 'mreq-comment-');
    pushId(selectors, comment, 'job-app-comment-');
    pushId(selectors, comment, 'job-app-pdf-');
    pushId(selectors, comment, 'hub-task-comment-');
  }

  const teamComment = params.get('teamComment')?.trim() || '';
  if (teamComment) pushId(selectors, teamComment, 'job-app-comment-');

  const item = params.get('item')?.trim() || '';
  if (item) pushId(selectors, item, 'bp-ready-');
  const bill = params.get('bill')?.trim() || '';
  if (bill) pushId(selectors, bill, 'bp-bill-');
  const deal = params.get('deal')?.trim() || '';
  if (deal && (item || bill)) pushId(selectors, deal, 'bp-project-');

  return selectors;
}

export function visibleEmbedTarget(selectors: string[]): HTMLElement | null {
  for (const selector of selectors) {
    let el: Element | null = null;
    try {
      el = document.querySelector(selector);
    } catch {
      el = null;
    }
    if (!(el instanceof HTMLElement)) continue;
    if (el.closest('[hidden]')) continue;
    if (el.getClientRects().length === 0) continue;
    return el;
  }
  return null;
}

export function scrollEmbedTargetIntoView(el: HTMLElement): void {
  el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
}

const FOCUS_MS = 12000;
const TICK_MS = 200;
const SETTLE_TICKS = 8;

/** Poll until the preview target is on screen, then correct while the page finishes opening. */
export function watchNotificationEmbedFocus(search: string): () => void {
  const selectors = notificationEmbedFocusSelectors(search);
  if (selectors.length === 0) return () => undefined;

  let stopped = false;
  let timer = 0;
  let settleTicks = 0;
  const started = Date.now();

  const tick = () => {
    if (stopped) return;
    const el = visibleEmbedTarget(selectors);
    if (el) {
      scrollEmbedTargetIntoView(el);
      settleTicks += 1;
      if (settleTicks >= SETTLE_TICKS) return;
    }
    if (Date.now() - started < FOCUS_MS) {
      timer = window.setTimeout(tick, TICK_MS);
    }
  };

  timer = window.setTimeout(tick, 40);
  return () => {
    stopped = true;
    window.clearTimeout(timer);
  };
}
